import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import ViewAgenda from '@mui/icons-material/ViewAgenda';
import ViewCarousel from '@mui/icons-material/ViewCarousel';
import ProviderCard from './building_blocks/ProviderCard';
import PageHeader from './building_blocks/PageHeader';
import SearchField from './SearchField';
import OutOfDeliveryAreaPanel from './building_blocks/OutOfDeliveryAreaPanel';
import { providersPathTo } from '../utils/Api';
import {
  originParamsFromUser,
  parseProvidersListResponse,
  isOutOfDeliveryArea,
} from '../utils/providerListParams';
import { UserContext } from '../contexts/UserContext';
import { SearchContext } from '../contexts/SearchContext';
import { buildSearchResultSections } from '../utils/rankProductsByTags';
import SearchResultSectionHeading from './building_blocks/SearchResultSectionHeading';
import {
  getSearchProductLayout,
  SEARCH_PRODUCT_LAYOUTS,
  setSearchProductLayout,
} from '../utils/searchProductLayoutStorage';
import { useNavigate } from 'react-router-dom';

const ProductLayoutToggle = ({ productLayout, onChange }) => (
  <Box
    sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
    aria-label="Visualização dos produtos"
  >
    <Tooltip title="Lista">
      <IconButton
        size="small"
        color={productLayout === SEARCH_PRODUCT_LAYOUTS.vertical ? 'primary' : 'default'}
        aria-label="Lista"
        aria-pressed={productLayout === SEARCH_PRODUCT_LAYOUTS.vertical}
        onClick={() => onChange(SEARCH_PRODUCT_LAYOUTS.vertical)}
      >
        <ViewAgenda />
      </IconButton>
    </Tooltip>
    <Tooltip title="Carrossel">
      <IconButton
        size="small"
        color={productLayout === SEARCH_PRODUCT_LAYOUTS.horizontal ? 'primary' : 'default'}
        aria-label="Carrossel"
        aria-pressed={productLayout === SEARCH_PRODUCT_LAYOUTS.horizontal}
        onClick={() => onChange(SEARCH_PRODUCT_LAYOUTS.horizontal)}
      >
        <ViewCarousel />
      </IconButton>
    </Tooltip>
  </Box>
);

const Search = () => {
  const [loading, setLoading] = useState(false);
  const [deliveryArea, setDeliveryArea] = useState(null);
  const [productLayout, setProductLayout] = useState(getSearchProductLayout);
  const { currentUser } = useContext(UserContext);
  const { searchResults, setSearchResults, searchTags, setSearchTags, setFromSearch } = useContext(SearchContext);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Busca - TudoPets.app';
  }, []);

  const handleSearchValueChange = (value) => {
    setSearchTags(value);
    if (value.length === 0) {
      setSearchResults([]);
      setDeliveryArea(null);
      return;
    }
    setLoading(true);
    const tags = value.map(tag => tag.name).join(',')
    const params = {
      t: tags,
      ...originParamsFromUser(currentUser),
    };
    providersPathTo.list({ params }).then((result) => {
      const { providers: list, deliveryArea: area } = parseProvidersListResponse(result.data);
      setLoading(false);
      setSearchResults(list);
      setDeliveryArea(area);
    });
  };

  const handleCardClick = (providerId) => {
    setFromSearch(true)
    navigate(`/provider/${providerId}`);
  }

  const handleProductLayoutChange = (nextLayout) => {
    if (nextLayout === productLayout) return;
    setProductLayout(nextLayout);
    setSearchProductLayout(nextLayout);
  };

  const resultSections = useMemo(
    () => buildSearchResultSections(searchResults, searchTags),
    [searchResults, searchTags]
  );

  const outOfDeliveryArea = isOutOfDeliveryArea(deliveryArea);
  const hasSearchQuery = searchTags.length > 0;
  const showNoProductResults = hasSearchQuery && !loading && !outOfDeliveryArea && resultSections.length === 0;
  const showLayoutToggle = hasSearchQuery && !loading && !outOfDeliveryArea && resultSections.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <PageHeader title="Busca" />

      <SearchField onChange={handleSearchValueChange} value={searchTags} />

      {loading ? (
        <Typography>Carregando...</Typography>
      ) : outOfDeliveryArea ? (
        <Box sx={{ alignSelf: 'stretch' }}>
          <OutOfDeliveryAreaPanel radiusKm={deliveryArea?.radius_km ?? 30} compact />
          <Button
            onClick={() => navigate('/home')}
            sx={{ mt: 2, textTransform: 'none', fontFamily: 'Nunito' }}
          >
            Voltar à Home
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, alignSelf: 'stretch' }}>
          {showNoProductResults && (
            <Typography sx={{ fontFamily: 'Nunito', color: 'text.secondary' }}>
              Nenhuma loja encontrada para essa busca.
            </Typography>
          )}
          {resultSections.map((section, sectionIndex) => {
            const showToggle = showLayoutToggle && sectionIndex === 0;

            return (
              <Box
                key={section.key}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignSelf: 'stretch' }}
              >
                {(searchTags.length > 0 || showToggle) && (
                  <SearchResultSectionHeading matchedTags={section.matchedTags}>
                    {showToggle && (
                      <ProductLayoutToggle
                        productLayout={productLayout}
                        onChange={handleProductLayoutChange}
                      />
                    )}
                  </SearchResultSectionHeading>
                )}
                {section.providers.map((provider) => (
                  <ProviderCard
                    key={`${section.key}-${provider.id}`}
                    provider={provider}
                    showProducts={true}
                    categories={false}
                    highlightTags={searchTags}
                    productLayout={productLayout}
                    onCardClick={() => handleCardClick(provider.id)}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default Search;
